/**
 * VehicleModificationsServiceModule — 2026-06-08 Phase 2D wire-up for the
 * canonical Q Manual VG35002 vehicle-modifications catalog REST surface.
 *
 * Wraps the briefing-room backend's vehicle-modifications-library endpoints
 * (authored in Phase 2C, commit e13519013). Lets the SPA list every mod with
 * compact-shape metadata and fetch a single entry's full data (description,
 * Q evaluation, game information, special rules, optional stats overrides).
 *
 * Routes (per briefing_room_backend/routes/vehicle_modifications.py):
 *   GET /api/modules/briefing-room/vehicle-modifications          → ModificationsManifest
 *   GET /api/modules/briefing-room/vehicle-modifications/{mod_id} → full mod JSON | 404
 *
 * Schema quirk: source seed file's entries-array key is `modifications` (not
 * `vehicle_modifications`). Phase 1A audit F5 added a `top_level_key` metadata
 * field documenting this; the backend manifest surfaces it. SPA passes the
 * shape through verbatim — `modifications` array at the top level matches the
 * route URL convention (`/vehicle-modifications`), no normalization needed.
 *
 * Structure points shape variance (per Phase 2A audit): the
 * `structure_points` field can be int / float / str ('half_of_craft_str',
 * 'per_str_point', 'na') / null. Typed as `number | string | null` here so
 * the drawer renders the raw shape without losing information.
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders().
 *
 * Plan reference: docs/20260608/20260608_PRE_COMPACT_18_HANDOFF_*.md §5.1.
 */
import { getAuthHeaders } from './authHeaders';

/** Per-modification summary returned by the manifest endpoint. */
export interface ModificationManifestEntry {
  id: string;
  name: string;
  page: number | null;
  /**
   * STR-point cost to host vehicle. See Phase 2A audit
   * modifications_structure_points_shapes for the 5 kinds: int (concrete
   * cost), 0.5 (half-STR default rule), 'half_of_craft_str' (scales with
   * host), 'per_str_point' (armor pricing — cost × host STR), 'na'
   * (not-applicable), null (advisory note like body-armor GM aid).
   */
  structure_points: number | string | null;
}

export interface ModificationsManifest {
  modifications: ModificationManifestEntry[];
  total: number;
  schema_version: string;
  source: string;
  /**
   * Surfaced from the seed's `top_level_key` metadata — always
   * 'modifications' for this file. Backend exposes it so SPA consumers
   * don't need to read the seed JSON directly to discover the shape.
   */
  top_level_key: string | null;
}

const BASE_PATH = '/api/modules/briefing-room/vehicle-modifications';

export class VehicleModificationsServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  /** List all vehicle modifications with compact metadata. */
  async list(): Promise<ModificationsManifest> {
    const resp = await this.fetchFn(BASE_PATH, {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `VehicleModificationsServiceModule: list HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as ModificationsManifest;
  }

  /**
   * Fetch a single modification's full entry. Returns null on 404 so
   * callers can branch on missing-vs-error.
   */
  async get(modId: string): Promise<Record<string, unknown> | null> {
    const resp = await this.fetchFn(
      `${BASE_PATH}/${encodeURIComponent(modId)}`,
      {
        method: 'GET',
        headers: { ...getAuthHeaders() },
      },
    );
    if (resp.status === 404) {
      return null;
    }
    if (!resp.ok) {
      throw new Error(
        `VehicleModificationsServiceModule: get HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as Record<string, unknown>;
  }
}
